/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, T, useTranslate } from "@adapptio/lib-components";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { numberToLocaleUnit } from "../../common/FormatNumber";
import { termsEditorCommon } from "../../terms/common";
import { Tooltip } from "./Tooltip";

/**
 * Component props
 */
export interface IDateTimeAgoProps {
	/** Date to compute diff to */
	date: Date;
	/** Translation term to put diff into */
	valueTerm?: string;
	/** Tooltip term */
	tooltipTerm?: string;
	/** Translation variables */
	tVars?: { [K: string]: unknown };
	/** Custom class name */
	className?: string;
}

/**
 * DateTimeAgo component
 */
export const DateTimeAgo: FC<IDateTimeAgoProps> = ({ date, className, valueTerm, tooltipTerm, tVars }) => {
	const t = useTranslate();

	const [ now, setNow ] = useState(DateTime.now());
	const rootElRef = useRef<HTMLSpanElement>();

	const dateStr = useMemo(() => {
		const start = DateTime.fromJSDate(date);
		const diff = now.diff(start, "days", { conversionAccuracy: "longterm" });

		const minutes = Math.floor(diff.as("minutes"));
		const hours = Math.floor(diff.as("hours"));
		const days = Math.floor(diff.as("days"));
		const weeks = Math.floor(diff.as("weeks"));
		const months = Math.floor(diff.as("months"));
		const years = Math.floor(diff.as("years"));

		if (years > 0) {
			return t("editor", numberToLocaleUnit(years, termsEditorCommon.dateTime.unitsAgo.year), {
				value: years
			});
		} else if (months > 0) {
			return t("editor", numberToLocaleUnit(months, termsEditorCommon.dateTime.unitsAgo.month), {
				value: months
			});
		} else if (weeks > 0) {
			return t("editor", numberToLocaleUnit(weeks, termsEditorCommon.dateTime.unitsAgo.week), {
				value: weeks
			});
		} else if (days > 0) {
			return t("editor", numberToLocaleUnit(days, termsEditorCommon.dateTime.unitsAgo.day), {
				value: days
			});
		} else if (hours > 0) {
			return t("editor", numberToLocaleUnit(hours, termsEditorCommon.dateTime.unitsAgo.hour), {
				value: hours
			});
		} else if (minutes > 0) {
			return t("editor", numberToLocaleUnit(minutes, termsEditorCommon.dateTime.unitsAgo.minute), {
				value: minutes
			});
		} else {
			return t("editor", termsEditorCommon.dateTime.unitsAgo.now, { value: minutes });
		}
	}, [ date, now ]);

	const atStr = useMemo(() => {
		return DateTime.fromJSDate(date).toLocaleString(DateTime.DATETIME_SHORT);
	}, [ date ]);

	useEffect(() => {
		const t = setInterval(() => {
			setNow(DateTime.now());
		}, 20000);

		return () => clearInterval(t);
	}, []);

	const baseClass = BEM("edt-date-time-ago");

	if (className) {
		baseClass.add(className);
	}

	const _tVars = tVars || {};

	return (
		<span className={baseClass.toClassName()} ref={rootElRef}>
			{valueTerm ? <T domain="editor" term={valueTerm} vars={{ ..._tVars, when: dateStr }} /> : dateStr}
			{tooltipTerm ? (
				<Tooltip for={rootElRef}>
					<T
						domain="editor"
						term={termsEditorCommon.resourceInfo.savedAtBy}
						vars={{ ..._tVars, at: atStr }}
					/>
				</Tooltip>
			) : null}
		</span>
	);
};
